import * as React from 'react';
import Tag from 'antd/lib/tag';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import { Divider } from 'antd/lib';
import {
    CheckCircleOutlined,
    InfoCircleOutlined,
    ThunderboltFilled,
    WarningFilled,
    ZoomInOutlined,
    ZoomOutOutlined,
} from '@ant-design/icons';
import {
    AmazonAppStoreData,
    AppleAppStoreData,
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppStoreSelectedBuildReason,
    AppStoreState,
    AppStoreType,
    CIBuildInfo,
    GooglePlayStoreData,
    HuaweiAppStoreData,
    ReleaseStepStoreReviewData,
    ReleaseStepSubmissionData,
    ReleaseStepTakeoffData,
    ReleaseStepType,
    ReviewAttachment,
    ReviewSubmissionItem,
    SamsungAppStoreData,
} from '@/types';
import { KickoffStepRunSummaryPanel } from './KickoffStepRunSummaryPanel';
import { FeatureReadinessStepRunSummaryPanel } from './FeatureReadinessStepRunSummaryPanel';
import { ReleaseCandidateStepRunSummaryPanel } from './ReleaseCandidateStepRunSummaryPanel';
import { RegressionTestingStepRunSummaryPanel } from './RegressionTestingStepRunSummaryPanel';
import { BetaTestingStepRunSummaryPanel } from './BetaTestingStepRunSummaryPanel';
import { MetadataStepRunSummaryPanel } from './MetadataStepRunSummaryPanel';
import { ScreenshotsStepRunSummaryPanel } from './ScreenshotsStepRunSummaryPanel';
import { ApprovalsStepRunSummaryPanel } from './ApprovalsStepRunSummaryPanel';
import { SubmissionStepRunSummaryPanel } from './SubmissionStepRunSummaryPanel';
import { StoreReviewStepRunSummaryPanel } from './StoreReviewStepRunSummaryPanel';
import { TakeoffStepRunSummaryPanel } from './TakeoffStepRunSummaryPanel';
import { CIDistributionStepRunSummaryPanel } from './CIDistributionStepRunSummaryPanel';
import { VCSDistributionStepRunSummaryPanel } from './VCSDistributionStepRunSummaryPanel';
import { RollbackBuildStepRunSummaryPanel } from './RollbackBuildStepRunSummaryPanel';
import { TranslationsStepRunSummaryPanel } from './TranslationsStepRunSummaryPanel';
import { RolloutStepRunSummaryPanel } from './RolloutStepRunSummaryPanel';
import { ReleaseCycleStepRunSummaryPanel } from './ReleaseCycleStepRunSummaryPanel';
import { StepRun } from '@/types/stepRun';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { Nullable } from '@/typescriptHelpers';
import {
    statusColorForReleaseStepStatus,
    titleForState,
} from '@/components/StepContentSubmission/StepContentSubmission';
import { isAppSubmitted, isGooglePlatform, oneOfSelectedAppStoreBuildMatchesCIBuild } from '@/helpers';
import AppStoreBuildComponent from '@/components/appStoreBuilds/AppStoreBuildComponent';
import {
    displayConsoleNameForAppStoreType,
    displayWarningMessageForSelectedAppStoreBuilds,
    reviewItemNameForType,
} from '@/helpers/displayHelpers';
import { iconForFileType } from '@/helpers/fileDisplayHelpers';
import { ReleaseTagState, ReleaseTag, TagData } from '@/types';
import Badge from 'antd/lib/badge';
import { BranchesOutlined } from '@ant-design/icons';
import commitIcon from '../../../../assets/commit-icon.svg';
import {
    buildStatusStringForBuild,
    statusColorForBuildStatus,
    shortCommitHashFromHash,
} from '@/helpers/displayHelpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '@/timeHelpers';
import { BuildInfo, WorkflowAndBranchData } from '@/types';
import Link from 'antd/lib/typography/Link';

export interface ArchetypeSpecificStepRunSummaryPanelProps {
    stepRun: StepRun;
    appId: string;
    shouldDisplayCompactVersion?: boolean;
}

export const getStepRunSummaryPanelByArchetype = (
    props: ArchetypeSpecificStepRunSummaryPanelProps,
): React.ReactElement | null => {
    switch (props.stepRun.type) {
        case ReleaseStepType.Kickoff:
            return <KickoffStepRunSummaryPanel {...props} />;
        case ReleaseStepType.FeatureReadiness:
            return <FeatureReadinessStepRunSummaryPanel {...props} />;
        case ReleaseStepType.ReleaseCandidate:
            return <ReleaseCandidateStepRunSummaryPanel {...props} />;
        case ReleaseStepType.RegressionTesting:
            return <RegressionTestingStepRunSummaryPanel {...props} />;
        case ReleaseStepType.BetaTesting:
            return <BetaTestingStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Metadata:
            return <MetadataStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Screenshots:
            return <ScreenshotsStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Approvals:
            return <ApprovalsStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Submission:
            return <SubmissionStepRunSummaryPanel {...props} />;
        case ReleaseStepType.StoreReview:
            return <StoreReviewStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Takeoff:
            return <TakeoffStepRunSummaryPanel {...props} />;
        case ReleaseStepType.CIDistribution:
            return <CIDistributionStepRunSummaryPanel {...props} />;
        case ReleaseStepType.VCSDistribution:
            return <VCSDistributionStepRunSummaryPanel {...props} />;
        case ReleaseStepType.RollbackBuild:
            return <RollbackBuildStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Translations:
            return <TranslationsStepRunSummaryPanel {...props} />;
        case ReleaseStepType.Rollout:
            return <RolloutStepRunSummaryPanel {...props} />;
        case ReleaseStepType.ReleaseCycle:
            return <ReleaseCycleStepRunSummaryPanel {...props} />;
        default:
            return null;
    }
};

export interface StepRunSummaryContainerProps {
    children: React.ReactNode;
    topBar: React.ReactNode;
}

export const StepRunSummaryContainer: React.FC<StepRunSummaryContainerProps> = ({ children, topBar }) => {
    return (
        <div style={{ backgroundColor: antGrey.grey2, padding: 16, borderRadius: 8 }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 12,
                    alignItems: 'center',
                    paddingBottom: children ? 12 : 0,
                }}
            >
                {topBar}
            </div>
            {children && (
                <div
                    style={{
                        width: '100%',
                        backgroundColor: runwayColors.white,
                        display: 'flex',
                        flexWrap: 'wrap',
                        flex: 1,
                        gap: 1,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: antGrey.grey4,
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export interface StepRunSummaryDetailItemProps {
    header: string;
    body: string | React.ReactNode;
    bodyStyle?: React.CSSProperties;
}

export const StepRunSummaryDetailItem: React.FC<StepRunSummaryDetailItemProps> = ({ header, body, bodyStyle }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '12px 20px',
            }}
        >
            <Text
                style={{
                    ...titleStyle,
                    color: antGrey.grey7,
                    fontSize: 10,
                }}
            >
                {header}
            </Text>
            {typeof body === 'string' ?
                <Text
                    style={{
                        color: antGrey.grey8,
                        textAlign: 'left',
                        ...bodyStyle,
                    }}
                >
                    {body}
                </Text>
            :   body}
        </div>
    );
};

export const StepRunSummaryDetailSeparator: React.FC = () => {
    return <div style={{ width: '1px', backgroundColor: antGrey.grey4, marginTop: 12, marginBottom: 12 }} />;
};

const summaryCardStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 250,
    padding: 16,
    backgroundColor: runwayColors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: antGrey.grey4,
};

export interface StepRunSummaryCardProps {
    children: React.ReactNode;
    style?: React.CSSProperties;
}

export const StepRunSummaryCard: React.FC<StepRunSummaryCardProps> = ({ children, style }) => {
    return <div style={{ ...summaryCardStyle, ...style }}>{children}</div>;
};
export interface CIBuildInfoTopBarProps {
    ciBuild?: BuildInfo;
    workflowData?: WorkflowAndBranchData;
    workflowTooltip: string;
}

export const CIBuildInfoTopBar: React.FC<CIBuildInfoTopBarProps> = ({ ciBuild, workflowData, workflowTooltip }) => {
    const [icon, alt] = iconAndAltForIntegrationType(workflowData?.integrationType);
    const buildStatusText = buildStatusStringForBuild(ciBuild);
    const tagColor = statusColorForBuildStatus(ciBuild?.buildStatus);
    const startedAtMoment = ciBuild?.buildStartedAt ? dayjs(ciBuild.buildStartedAt) : null;

    const formatStartedTime = () => {
        if (startedAtMoment) {
            return `started ${startedAtMoment.format(getLocaleDateTimeFormat('short'))}`;
        }
        return null;
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Space align="center" size={12}>
                {icon && <img src={icon} alt={alt} style={{ height: 20 }} />}
                <Tag
                    style={{
                        ...titleStyle,
                        fontSize: 10,
                        lineHeight: '22px',
                        margin: 0,
                        color: tagColor,
                        background: 'white',
                        border: `1px solid ${tagColor}`,
                    }}
                >
                    {buildStatusText}
                </Tag>
                <Space split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />} align="center">
                    {ciBuild?.buildIdentifier &&
                        (ciBuild.buildUrl ?
                            <Link href={ciBuild.buildUrl} target="_blank" strong>
                                Build #{ciBuild.buildIdentifier}
                            </Link>
                        :   <Text style={{ color: antGrey.grey8 }} strong>
                                Build #{ciBuild.buildIdentifier}
                            </Text>)}
                    {formatStartedTime() && <Text style={{ color: antGrey.grey7 }}>{formatStartedTime()}</Text>}
                </Space>
            </Space>
            <Space align="center" size={8}>
                {workflowData?.workflowUrl ?
                    <Link href={workflowData.workflowUrl} target="_blank" style={{ color: runwayColors.info }}>
                        {workflowData.workflowName ?? '--'}
                    </Link>
                :   <Text style={{ color: runwayColors.info }}>{workflowData?.workflowName ?? '--'}</Text>}
                <Tooltip title={workflowTooltip}>
                    <InfoCircleOutlined style={{ color: antGrey.grey6, cursor: 'pointer' }} />
                </Tooltip>
            </Space>
        </div>
    );
};

export interface CIBuildInfoDetailItemsProps {
    ciBuild?: BuildInfo;
    workflowData?: WorkflowAndBranchData;
}

export const CIBuildInfoBranchItem: React.FC<CIBuildInfoDetailItemsProps> = ({ ciBuild, workflowData }) => {
    const branch = ciBuild?.branchName ?? workflowData?.branch ?? '--';
    return (
        <Space align="center" size={8}>
            <BranchesOutlined style={{ color: antGrey.grey7 }} />
            <Text style={{ color: antGrey.grey8, fontFamily: 'monospace' }}>{branch}</Text>
        </Space>
    );
};

export const CIBuildInfoCommitItem: React.FC<CIBuildInfoDetailItemsProps> = ({ ciBuild, workflowData }) => {
    const commit = ciBuild?.commit ?? workflowData?.headCommit ?? '--';
    const shortHash = shortCommitHashFromHash(commit);
    return (
        <Space align="center" size={8}>
            <img src={commitIcon} alt="commit" style={{ width: 14, height: 14 }} />
            <Text style={{ color: antGrey.grey8, fontFamily: 'monospace' }}>{shortHash}</Text>
        </Space>
    );
};

export const CIBuildInfoMessageItem: React.FC<CIBuildInfoDetailItemsProps> = ({ ciBuild, workflowData }) => {
    const commitMessage = ciBuild?.commitMessage ?? workflowData?.headCommitMessage ?? '--';
    return (
        <Text style={{ color: antGrey.grey8, maxWidth: 600 }} ellipsis={{ tooltip: commitMessage }}>
            {commitMessage}
        </Text>
    );
};

export const getTagStateColor = (releaseTagState: ReleaseTagState): string => {
    switch (releaseTagState) {
        case ReleaseTagState.Tagged:
            return runwayColors.statusGreen;
        case ReleaseTagState.TagNeeded:
        case ReleaseTagState.TagNeededNameUnresolved:
        case ReleaseTagState.NotTaggable:
        default:
            return runwayColors.statusYellow;
    }
};

export interface TagDetailSectionProps {
    tagData: TagData;
}

const getTagStatusLabel = (releaseTagState: ReleaseTagState, releaseTag?: ReleaseTag): string => {
    switch (releaseTagState) {
        case ReleaseTagState.Tagged:
            return releaseTag?.authorName ? `Tagged by ${releaseTag.authorName}` : 'Tagged';
        case ReleaseTagState.TagNeeded:
        case ReleaseTagState.TagNeededNameUnresolved:
            return 'Pending';
        case ReleaseTagState.NotTaggable:
        default:
            return 'Not detected';
    }
};

export const TagDetailSection: React.FC<TagDetailSectionProps> = ({ tagData }) => {
    const { expectedReleaseTagName, releaseTagState, releaseTag } = tagData;
    const stateColor = getTagStateColor(releaseTagState);
    const statusLabel = getTagStatusLabel(releaseTagState, releaseTag);
    const isTagged = releaseTagState === ReleaseTagState.Tagged && releaseTag;
    const tagName = releaseTag?.name ?? expectedReleaseTagName ?? '--';

    return (
        <>
            <div style={{ flexBasis: '100%', height: 0 }} />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: '12px 20px',
                    width: '100%',
                    flex: 1,
                    borderTop: `1px solid ${antGrey.grey4}`,
                }}
            >
                <Space align="center" size={6}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        TAG
                    </Text>
                    <Tooltip title={statusLabel}>
                        <Badge color={stateColor} />
                    </Tooltip>
                </Space>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {isTagged && releaseTag?.url ?
                        <Link href={releaseTag.url} target="_blank" code style={{ fontFamily: 'monospace' }}>
                            {tagName}
                        </Link>
                    :   <Text code style={{ color: antGrey.grey7, fontFamily: 'monospace' }}>
                            {tagName}
                        </Text>
                    }
                </div>
            </div>
        </>
    );
};

// Shared constants
export const MAX_VISIBLE_BUILDS = 1;

// Shared helper functions
export const useSRRTopBarData = (stepRun: StepRun) => {
    const stepData: ReleaseStepSubmissionData | ReleaseStepStoreReviewData | ReleaseStepTakeoffData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const appStoreData = stepData.appStoreData as AppleAppStoreData;
    const googleStoreData = stepData.appStoreData as GooglePlayStoreData;
    const amazonData = stepData.appStoreData as AmazonAppStoreData;
    const huaweiData = stepData.appStoreData as HuaweiAppStoreData;
    const samsungData = stepData.appStoreData as SamsungAppStoreData;

    const isSubmitted = isAppSubmitted(
        stepData.appStoreType,
        appStoreData?.versionReviewSubmissionItem?.state,
        appStoreData?.relevantReviewSubmission?.state,
        googleStoreData?.state,
        amazonData?.state,
        huaweiData?.state,
        samsungData?.state,
    );

    const appleAppStoreState = appStoreData?.versionReviewSubmissionItem?.state;
    const googleAppStoreState = googleStoreData?.state;
    const amazonAppStoreState = amazonData?.state;
    const huaweiAppStoreState = huaweiData?.state;
    const samsungAppStoreState = samsungData?.state;

    let appStoreState: Nullable<AppStoreState>;
    if (appleAppStoreState) {
        appStoreState = appleAppStoreState;
    } else if (googleAppStoreState) {
        appStoreState = googleAppStoreState;
    } else if (amazonAppStoreState) {
        appStoreState = amazonAppStoreState;
    } else if (huaweiAppStoreState) {
        appStoreState = huaweiAppStoreState;
    } else if (samsungAppStoreState) {
        appStoreState = samsungAppStoreState;
    }

    const hasSelectedBuild = stepData.appStoreSelectedBuilds?.length > 0;

    return {
        stepData,
        appStoreData,
        googleStoreData,
        amazonData,
        huaweiData,
        samsungData,
        isSubmitted,
        appStoreState,
        hasSelectedBuild,
    };
};

export const createSRRTopBar = (
    stepRun: StepRun,
    stepData: ReleaseStepSubmissionData,
    appStoreData: AppleAppStoreData,
    isSubmitted: boolean,
    appStoreState: Nullable<AppStoreState>,
    hasSelectedBuild: boolean,
) => {
    return (
        <>
            <SubmissionStatusTag
                stepRun={stepRun}
                isSubmitted={isSubmitted}
                appStoreType={stepData.appStoreType}
                appStoreState={appStoreState}
                isReleaseFromScraper={false}
                hasSelectedBuild={hasSelectedBuild}
            />
            <SelectedBuildsDisplay
                builds={stepData.appStoreSelectedBuilds ?? []}
                platform={stepData.platform}
                appStoreType={stepData.appStoreType}
                isSubmitted={isSubmitted}
                activeRCBuild={stepData.latestSuccessfulBuild}
                prodBuild={stepData.prodBuild}
            />
            {appStoreData?.relevantReviewSubmission?.reviewSubmissionItems?.length > 1 && (
                <>
                    <Divider type="vertical" style={{ height: 22 }} />
                    <SubmissionItemsCount
                        reviewSubmissionItems={appStoreData?.relevantReviewSubmission?.reviewSubmissionItems}
                    />
                </>
            )}
        </>
    );
};
export const getTooltipTextForBuildSelectionReason = (
    buildNumber: string,
    selectedBuildReason: Nullable<AppStoreSelectedBuildReason>,
    autoSelectedFromTrack: Nullable<string>,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    isSubmitted: boolean,
): Nullable<string> => {
    if (!selectedBuildReason || selectedBuildReason === AppStoreSelectedBuildReason.Rollback) {
        return null;
    }

    const consoleName = displayConsoleNameForAppStoreType(appStoreType);

    switch (selectedBuildReason) {
        case AppStoreSelectedBuildReason.AutoSelection: {
            const endingText =
                isGooglePlatform(platform) && autoSelectedFromTrack ?
                    ` from the configured track '${autoSelectedFromTrack}'`
                :   '';
            return isSubmitted ?
                    `Runway automatically selected build (#${buildNumber})${endingText} for submission.`
                :   `Runway automatically selected the latest build (#${buildNumber})${endingText}.`;
        }
        case AppStoreSelectedBuildReason.ManualRunwaySelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build (#${buildNumber}) was manually selected in Runway.`
                :   `The selected build (#${buildNumber}) was manually changed in Runway.`;
        case AppStoreSelectedBuildReason.ManualStoreSelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build (#${buildNumber}) was manually selected in ${consoleName}.`
                :   `The selected ${consoleName} build (#${buildNumber}) was manually changed in ${consoleName}.`;
        case AppStoreSelectedBuildReason.ManualRCSelection:
            return isSubmitted ?
                    `The submitted ${consoleName} build's (#${buildNumber}) associated CI build was manually selected in the CI step.`
                :   `The selected ${consoleName} build's (#${buildNumber}) associated CI build was manually selected in the CI step.`;
        default:
            return null;
    }
};

export const getBuildSelectionWarnings = (
    selectedBuilds: AppStoreAvailableBuild[],
    activeRCBuild: Nullable<CIBuildInfo>,
    prodBuild: Nullable<CIBuildInfo>,
): string[] => {
    const warnings: string[] = [];

    if (selectedBuilds.length === 0) {
        return warnings;
    }

    if (activeRCBuild?.associatedAppStoreBuild) {
        const matchesActiveRC = oneOfSelectedAppStoreBuildMatchesCIBuild(selectedBuilds, activeRCBuild);
        if (!matchesActiveRC) {
            warnings.push(displayWarningMessageForSelectedAppStoreBuilds(selectedBuilds, activeRCBuild, false));
        }
    }

    if (prodBuild?.associatedAppStoreBuild) {
        const matchesProdBuild = oneOfSelectedAppStoreBuildMatchesCIBuild(selectedBuilds, prodBuild);
        if (!matchesProdBuild) {
            warnings.length = 0;
            warnings.push(displayWarningMessageForSelectedAppStoreBuilds(selectedBuilds, prodBuild, true));
        } else {
            warnings.length = 0;
        }
    }

    return warnings;
};

export const getSubmissionItemCountsByType = (
    items: ReviewSubmissionItem[],
): Map<AppStoreReviewSubmissionItemType, number> => {
    const counts = new Map<AppStoreReviewSubmissionItemType, number>();

    for (const item of items) {
        const currentCount = counts.get(item.type) ?? 0;
        counts.set(item.type, currentCount + 1);
    }

    return counts;
};

// Shared components
export interface BuildSelectionStatusIconProps {
    selectedBuilds: AppStoreAvailableBuild[];
    activeRCBuild: Nullable<CIBuildInfo>;
    prodBuild: Nullable<CIBuildInfo>;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    isSubmitted: boolean;
}

export const BuildSelectionStatusIcon: React.FC<BuildSelectionStatusIconProps> = ({
    selectedBuilds,
    activeRCBuild,
    prodBuild,
    platform,
    appStoreType,
    isSubmitted,
}) => {
    if (!selectedBuilds || selectedBuilds.length === 0) {
        return null;
    }

    const warnings = getBuildSelectionWarnings(selectedBuilds, activeRCBuild, prodBuild);
    const hasWarnings = warnings.length > 0;

    const firstBuild = selectedBuilds[0];
    const isAutoSelection = firstBuild.selectedBuildReason === AppStoreSelectedBuildReason.AutoSelection;

    const selectionReasonTooltip = getTooltipTextForBuildSelectionReason(
        firstBuild.appStoreBuild.buildNumber,
        firstBuild.selectedBuildReason,
        firstBuild.autoSelectedFromTrack,
        platform,
        appStoreType,
        isSubmitted,
    );

    // Priority 1: Show warning icon if there are warnings
    if (hasWarnings) {
        const tooltipContent = (
            <Space direction="vertical" size={4}>
                {warnings.map((warning, idx) => (
                    <span key={idx}>{warning}</span>
                ))}
            </Space>
        );

        return (
            <Tooltip title={tooltipContent}>
                <WarningFilled style={{ color: runwayColors.statusYellow, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        );
    }

    // Priority 2: Show lightning bolt if auto-selected
    if (isAutoSelection) {
        return (
            <Tooltip title={selectionReasonTooltip}>
                <ThunderboltFilled style={{ color: runwayColors.info, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        );
    }

    // Priority 3: Show green check for everything else
    return (
        <Tooltip title={selectionReasonTooltip ?? 'Build selected'}>
            <CheckCircleOutlined style={{ color: runwayColors.statusGreen, fontSize: 14, cursor: 'pointer' }} />
        </Tooltip>
    );
};

export interface SelectedBuildsDisplayProps {
    builds: AppStoreAvailableBuild[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    isSubmitted: boolean;
    activeRCBuild: Nullable<CIBuildInfo>;
    prodBuild: Nullable<CIBuildInfo>;
}

export const SelectedBuildsDisplay: React.FC<SelectedBuildsDisplayProps> = ({
    builds,
    platform,
    appStoreType,
    isSubmitted,
    activeRCBuild,
    prodBuild,
}) => {
    if (!builds || builds.length === 0) {
        return null;
    }

    const visibleBuilds = builds.slice(0, MAX_VISIBLE_BUILDS);
    const remainingBuilds = builds.slice(MAX_VISIBLE_BUILDS);
    const remainingCount = remainingBuilds.length;

    return (
        <>
            {visibleBuilds.map((build) => (
                <AppStoreBuildComponent
                    key={build.appStoreBuild.id}
                    {...build}
                    styleOverrides={{
                        backgroundColor: antGrey.grey4,
                    }}
                />
            ))}
            {remainingCount > 0 && (
                <Popover
                    content={
                        <Space direction="vertical" size={8}>
                            {remainingBuilds.map((build) => (
                                <AppStoreBuildComponent
                                    key={build.appStoreBuild.id}
                                    {...build}
                                    styleOverrides={{
                                        backgroundColor: antGrey.grey4,
                                    }}
                                />
                            ))}
                        </Space>
                    }
                    trigger="hover"
                    placement="bottom"
                >
                    <Tag
                        style={{
                            ...titleStyle,
                            fontSize: 10,
                            lineHeight: '22px',
                            margin: 0,
                            color: antGrey.grey7,
                            background: antGrey.grey4,
                            border: `1px solid ${antGrey.grey5}`,
                            cursor: 'pointer',
                        }}
                    >
                        +{remainingCount}
                    </Tag>
                </Popover>
            )}
            <BuildSelectionStatusIcon
                selectedBuilds={builds}
                activeRCBuild={activeRCBuild}
                prodBuild={prodBuild}
                platform={platform}
                appStoreType={appStoreType}
                isSubmitted={isSubmitted}
            />
        </>
    );
};

export interface SubmissionItemsCountProps {
    reviewSubmissionItems: Nullable<ReviewSubmissionItem[]>;
}

export const SubmissionItemsCount: React.FC<SubmissionItemsCountProps> = ({ reviewSubmissionItems }) => {
    const filteredItems = React.useMemo(
        () => reviewSubmissionItems?.filter((item) => item.state !== AppStoreReviewSubmissionItemState.Removed) ?? [],
        [reviewSubmissionItems],
    );

    if (filteredItems.length === 0) {
        return null;
    }

    const countsByType = getSubmissionItemCountsByType(filteredItems);

    const tooltipContent = (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
            {Array.from(countsByType.entries()).map(([type, count]) => (
                <li key={type}>
                    <Text style={{ color: 'white' }}>
                        {count} {reviewItemNameForType(type).toLowerCase()}
                        {count > 1 ? 's' : ''}
                    </Text>
                </li>
            ))}
        </ul>
    );

    return (
        <Space align="center" size={8}>
            <Text style={{ color: antGrey.grey7, fontSize: 12, fontWeight: 600 }}>{filteredItems.length} items</Text>
            <Tooltip title={tooltipContent}>
                <InfoCircleOutlined style={{ color: runwayColors.info, fontSize: 14, cursor: 'pointer' }} />
            </Tooltip>
        </Space>
    );
};

export interface SubmissionStatusTagProps {
    stepRun: StepRun;
    isSubmitted: boolean;
    appStoreType: AppStoreType;
    appStoreState: Nullable<AppStoreState>;
    isReleaseFromScraper: boolean;
    hasSelectedBuild: boolean;
}

export const SubmissionStatusTag: React.FC<SubmissionStatusTagProps> = ({
    stepRun,
    isSubmitted,
    appStoreType,
    appStoreState,
    isReleaseFromScraper,
    hasSelectedBuild,
}) => {
    const color = statusColorForReleaseStepStatus(stepRun.status, isReleaseFromScraper);
    const statusText = titleForState(
        isSubmitted,
        appStoreType,
        stepRun.status,
        appStoreState,
        isReleaseFromScraper,
        hasSelectedBuild,
    );

    return (
        <Tag
            style={{
                ...titleStyle,
                fontSize: 10,
                lineHeight: '22px',
                margin: 0,
                color: color,
                background: runwayColors.white,
                border: `1px solid ${color}`,
            }}
        >
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    top: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    alignItems: 'stretch',
                    gap: 8,
                }}
            >
                {statusText}
            </div>
        </Tag>
    );
};

export interface AppReviewInformationDetailItemProps {
    reviewNotes: Nullable<string>;
    attachments: Nullable<ReviewAttachment[]>;
    contactFirstName: Nullable<string>;
    contactLastName: Nullable<string>;
    contactEmail: Nullable<string>;
    contactPhone: Nullable<string>;
    demoAccountRequired: Nullable<boolean>;
    shouldDisplayCompactVersion?: boolean;
}

export const AppReviewInformationDetailItem: React.FC<AppReviewInformationDetailItemProps> = ({
    reviewNotes,
    attachments,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    demoAccountRequired,
    shouldDisplayCompactVersion,
}) => {
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

    const getCollapsedBody = () => {
        return (
            <Space split={<Divider type="vertical" />} align="center" wrap>
                {reviewNotes ?
                    <Text style={{ color: antGrey.grey7, maxWidth: 600 }} ellipsis={{ tooltip: reviewNotes }}>
                        {reviewNotes}
                    </Text>
                :   <Text style={{ color: antGrey.grey7, fontStyle: 'italic' }}>No notes</Text>}
                {attachments?.map((attachment) => (
                    <Space key={attachment.fileName} align="center" size={4} direction="horizontal" wrap>
                        {iconForFileType(attachment.fileName)}
                        <Text style={{ color: antGrey.grey7 }}>{attachment.fileName}</Text>
                    </Space>
                ))}
            </Space>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '12px 20px',
                width: shouldDisplayCompactVersion ? undefined : '100%',
                flex: 1,
                position: 'relative',
            }}
        >
            <div style={{ position: 'absolute', right: 12, top: 8, zIndex: 2 }}>
                <Button
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ width: 28 }}
                    icon={
                        isExpanded ?
                            <ZoomOutOutlined style={{ color: antGrey.grey6 }} />
                        :   <ZoomInOutlined style={{ color: antGrey.grey6 }} />
                    }
                    type="link"
                />
            </div>

            <Text
                style={{
                    ...titleStyle,
                    color: antGrey.grey7,
                    fontSize: 10,
                }}
            >
                APP REVIEW INFORMATION
            </Text>

            {getCollapsedBody()}

            <div
                style={{
                    borderColor: antGrey.grey4,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    padding: '12px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    borderRadius: 8,
                    backgroundColor: runwayColors.white,
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                    width: 'calc(100% + 2px)',
                    left: -1,
                    top: -1,
                    zIndex: 3,
                    position: 'absolute',
                    transition: 'all 0.2s ease-in-out',
                    ...(isExpanded ? { opacity: 1 } : { opacity: 0, pointerEvents: 'none' as const }),
                }}
            >
                <div style={{ position: 'absolute', right: 12, top: 8, zIndex: 2 }}>
                    <Button
                        onClick={() => setIsExpanded(false)}
                        style={{ width: 28 }}
                        icon={<ZoomOutOutlined style={{ color: antGrey.grey6 }} />}
                        type="link"
                    />
                </div>

                <Text
                    style={{
                        ...titleStyle,
                        color: antGrey.grey7,
                        fontSize: 10,
                    }}
                >
                    APP REVIEW INFORMATION
                </Text>

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        REVIEW NOTES
                    </Text>
                    <Text style={{ color: antGrey.grey8 }}>
                        {reviewNotes || <span style={{ fontStyle: 'italic', color: antGrey.grey6 }}>No notes</span>}
                    </Text>
                </div>

                {attachments && attachments.length > 0 && (
                    <>
                        <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Text
                                style={{
                                    ...titleStyle,
                                    color: antGrey.grey7,
                                    fontSize: 10,
                                }}
                            >
                                ATTACHMENTS
                            </Text>
                            <Space direction="vertical" size={4}>
                                {attachments.map((attachment) => (
                                    <Space key={attachment.fileName} align="center" size={8}>
                                        {iconForFileType(attachment.fileName)}
                                        <Text style={{ color: antGrey.grey8 }}>{attachment.fileName}</Text>
                                    </Space>
                                ))}
                            </Space>
                        </div>
                    </>
                )}

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        CONTACT
                    </Text>
                    {contactFirstName || contactLastName || contactEmail || contactPhone ?
                        <Space direction="vertical" size={0}>
                            {(contactFirstName || contactLastName) && (
                                <Text style={{ color: antGrey.grey8 }}>
                                    {[contactFirstName, contactLastName].filter(Boolean).join(' ')}
                                </Text>
                            )}
                            {contactEmail && <Text style={{ color: antGrey.grey8 }}>{contactEmail}</Text>}
                            {contactPhone && <Text style={{ color: antGrey.grey8 }}>{contactPhone}</Text>}
                        </Space>
                    :   <Text style={{ fontStyle: 'italic', color: antGrey.grey6 }}>No contact info</Text>}
                </div>

                <div style={{ width: '100%', height: 1, backgroundColor: antGrey.grey4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Text
                        style={{
                            ...titleStyle,
                            color: antGrey.grey7,
                            fontSize: 10,
                        }}
                    >
                        SIGN IN REQUIRED
                    </Text>
                    <Text style={{ color: antGrey.grey8 }}>
                        {demoAccountRequired != null ?
                            demoAccountRequired ?
                                'Yes'
                            :   'No'
                        :   <span style={{ fontStyle: 'italic', color: antGrey.grey6 }}>Not set</span>}
                    </Text>
                </div>
            </div>
        </div>
    );
};
